import { zodResolver } from '@hookform/resolvers/zod';
import * as m from '@staiter/i18n';
import { PasswordChangeRequest, type PasswordChangeRequest as PwdReq } from '@staiter/shared';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Button } from '~/components/primitives/Button';
import { Input } from '~/components/primitives/Input';
import { changePassword } from '~/lib/api/auth';
import { ApiRequestError } from '~/lib/api/client';
import { DEFAULT_LANDING_ROUTE } from '~/lib/rbac';
import { useAuthStore } from '~/store/auth';
import styles from './_auth.force-password-change.module.css';

// Модальный flow — пользователь НЕ может перейти на другие экраны до смены пароля.
// Реализуется через `beforeLoad` в `_auth.tsx` (редирект сюда, если mustReset=true).
export const Route = createFileRoute('/_auth/force-password-change')({
  component: ForcePasswordChangePage,
});

function ForcePasswordChangePage(): JSX.Element {
  const navigate = useNavigate();
  const [rateLimitSeconds, setRateLimitSeconds] = useState<number | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<PwdReq>({
    resolver: zodResolver(PasswordChangeRequest),
    defaultValues: { current_password: '', new_password: '', confirm_password: '' },
  });

  const onSubmit = async (values: PwdReq): Promise<void> => {
    setServerError(null);
    try {
      await changePassword(values);
      // На success — сбрасываем флаг и пускаем на «Задачи» (каноническая посадка).
      useAuthStore.setState({ mustReset: false });
      await navigate({ to: DEFAULT_LANDING_ROUTE as '/' });
    } catch (err) {
      if (err instanceof ApiRequestError) {
        if (err.status === 429 && err.retry_after) {
          setRateLimitSeconds(err.retry_after);
          startCountdown(err.retry_after, setRateLimitSeconds);
          return;
        }
        setServerError(err.message || m.force_pwd_error_generic());
        return;
      }
      setServerError(m.login_network_error());
    }
  };

  return (
    <main
      className={styles.page}
      role="dialog"
      aria-modal="true"
      aria-labelledby="force-reset-title"
    >
      <section className={styles.card}>
        <h1 id="force-reset-title" className={styles.title}>
          {m.force_pwd_title()}
        </h1>
        <p className={styles.lede}>{m.force_pwd_lede()}</p>

        <form className={styles.form} onSubmit={handleSubmit(onSubmit)} noValidate>
          <Input
            label={m.force_pwd_current()}
            type="password"
            autoComplete="current-password"
            autoFocus
            error={errors.current_password?.message}
            {...register('current_password')}
          />
          <Input
            label={m.force_pwd_new()}
            type="password"
            autoComplete="new-password"
            hint={m.force_pwd_new_hint()}
            error={errors.new_password?.message}
            {...register('new_password')}
          />
          <Input
            label={m.force_pwd_confirm()}
            type="password"
            autoComplete="new-password"
            error={errors.confirm_password?.message}
            {...register('confirm_password')}
          />

          {rateLimitSeconds !== null && (
            <p className={styles.rateLimit} role="status" aria-live="polite">
              {m.login_rate_limit({ seconds: rateLimitSeconds })}
            </p>
          )}
          {serverError && (
            <p className={styles.serverError} role="alert">
              {serverError}
            </p>
          )}

          <Button
            type="submit"
            variant="primary"
            disabled={isSubmitting || rateLimitSeconds !== null}
            loading={isSubmitting}
          >
            {m.force_pwd_submit()}
          </Button>
        </form>
      </section>
    </main>
  );
}

function startCountdown(initial: number, setter: (v: number | null) => void): void {
  let remaining = initial;
  const interval = setInterval(() => {
    remaining -= 1;
    if (remaining <= 0) {
      clearInterval(interval);
      setter(null);
    } else {
      setter(remaining);
    }
  }, 1000);
}
